import { Bold as OriginalBold } from 'element-tiptap'
import html2canvas from 'html2canvas'
import QRCode from 'qrcode'
import CustomEnglishParseMenuButton from './MenuButton'
import { parseEnglishSentence, containsEnglish, extractEnglishSentences } from '@/utils/cozeAgent'

// 解析智能体返回的结果，提取分层解析的句子和翻译
function extractSentenceFromResult(result) {
  console.log('提取句子结构，输入数据类型:', typeof result, result)
  
  // 处理空值或无效值
  if (!result) {
    console.warn('extractSentenceFromResult: 输入数据为空')
    return { sentence: '', allTrans: '', mainTrans: '' }
  }
  
  // 尝试处理字符串类型的结果（可能是JSON字符串）
  if (typeof result === 'string') {
    try {
      const parsed = JSON.parse(result)
      console.log('成功解析JSON字符串:', parsed)
      // 递归调用自身处理解析后的对象
      return extractSentenceFromResult(parsed)
    } catch (e) {
      console.log('不是有效的JSON字符串，按普通文本处理')
      // 查找"分层解析："后面的内容
      const match = result.match(/分层解析：(.+?)(?:","from_module|$)/);
      if (match) {
        return {
          sentence: match[1].trim(),
          allTrans: '',
          mainTrans: ''
        };
      }

      // 如果没有找到分层解析，尝试直接提取句子
      const directMatch = result.match(/^([^"]+)/);
      if (directMatch) {
        return {
          sentence: directMatch[1].trim(),
          allTrans: '',
          mainTrans: ''
        };
      }

      // 作为普通文本返回
      return {
        sentence: result,
        allTrans: '',
        mainTrans: ''
      };
    }
  }
  
  // 适配新的数据格式 - 直接包含output和content的对象
  if (result && result.output && result.output.content) {
    function convertCozeToParenthesisString(content) {
      return content.map(item => {
        if (item.type === 'text') {
          // 保持原始格式，包括加粗标记
          return item.value;
        }
        if (item.type === 'collapsible' && item.content) {
          return `(${convertCozeToParenthesisString(item.content)})`;
        }
        return '';
      }).join('');
    }

    const sentence = convertCozeToParenthesisString(result.output.content);
    return {
      sentence: sentence,
      allTrans: result.all_trans || '',
      mainTrans: result.main_trans || ''
    };
  }

  // 处理已经解析过的结果对象
  if (typeof result === 'object' && result.sentence) {
    return {
      sentence: result.sentence,
      allTrans: result.allTrans || result.all_trans || '',
      mainTrans: result.mainTrans || result.main_trans || ''
    };
  }
  
  // 处理coze原始返回数据 - 包含data字段的对象
  if (typeof result === 'object' && result.data) {
    let data;
    try {
      // 处理data字段可能是字符串的情况
      data = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
    } catch (e) {
      console.error('解析data字段失败:', e);
      return { sentence: '', allTrans: '', mainTrans: '' };
    }
    
    // 如果data对象包含output和content
    if (data && data.output && data.output.content) {
      function convertCozeToParenthesisString(content) {
        if (!Array.isArray(content)) {
          console.warn('content不是数组:', content);
          return '';
        }
        
        // 新的处理逻辑：将连续的text和collapsible组合成带括号的结构
        let result = '';
        let i = 0;
        
        while (i < content.length) {
          const item = content[i];
          
          if (!item || typeof item !== 'object') {
            i++;
            continue;
          }
          
          if (item.type === 'text') {
            result += item.value || '';
          } else if (item.type === 'collapsible') {
            // 处理collapsible类型
            if (item.content && Array.isArray(item.content)) {
              // 如果有content，递归处理
              const innerContent = convertCozeToParenthesisString(item.content);
              result += innerContent ? `(${innerContent})` : '()';
            } else if (item.value && item.value !== null) {
              // 如果有value，直接使用
              result += `(${item.value})`;
            } else {
              // 如果value为null，查看前后的text项目，尝试构建括号结构
              // 这种情况下，collapsible可能表示一个空的可折叠区域
              // 我们需要根据上下文来判断如何处理
              
              // 简单处理：如果collapsible的value为null，我们查看是否有相邻的text
              // 如果前面有text且后面也有text，可能需要将中间部分包装在括号中
              if (i > 0 && i < content.length - 1) {
                const prevItem = content[i - 1];
                const nextItem = content[i + 1];
                
                if (prevItem && prevItem.type === 'text' && 
                    nextItem && nextItem.type === 'text') {
                  // 这种情况下，collapsible可能表示一个分隔点
                  // 我们可以在这里添加一个空的括号对或者特殊标记
                  result += '()';
                } else {
                  result += '()';
                }
              } else {
                result += '()';
              }
            }
          }
          
          i++;
        }
        
        return result;
      }
      
      const sentence = convertCozeToParenthesisString(data.output.content);
      console.log('转换后的句子:', sentence);
      
      return {
        sentence: sentence,
        allTrans: data.all_trans || '',
        mainTrans: data.main_trans || ''
      };
    }
    
    // 如果data字段是字符串但不包含预期结构
    if (typeof data === 'string') {
      return {
        sentence: data,
        allTrans: '',
        mainTrans: ''
      };
    }
  }
  
  // 处理coze原始返回的code和data结构
  if (typeof result === 'object' && 'code' in result && result.code === 0) {
    if (result.data) {
      // 递归处理data字段
      return extractSentenceFromResult(result.data);
    }
  }

  // 无法识别的格式，尝试最后的处理方式
  console.warn('无法识别的数据格式:', result)
  
  // 如果是对象，尝试查找可能的句子字段
  if (typeof result === 'object') {
    // 尝试查找常见字段名
    const possibleFields = ['sentence', 'text', 'content', 'value', 'result', 'output', 'data'];
    for (const field of possibleFields) {
      if (result[field]) {
        if (typeof result[field] === 'string') {
          return {
            sentence: result[field],
            allTrans: result.allTrans || result.all_trans || '',
            mainTrans: result.mainTrans || result.main_trans || ''
          };
        } else if (typeof result[field] === 'object') {
          // 递归处理嵌套对象
          const extracted = extractSentenceFromResult(result[field]);
          if (extracted && extracted.sentence) {
            return extracted;
          }
        }
      }
    }
    
    // 如果没有找到有效字段，转为JSON字符串
    return {
      sentence: JSON.stringify(result),
      allTrans: '',
      mainTrans: ''
    };
  }
  
  // 最后的兜底方案
  return {
    sentence: String(result),
    allTrans: '',
    mainTrans: ''
  };
}

// 定义不同层级的颜色
const levelColors = [
  { border: '#2196F3', bg: 'rgba(33, 150, 243, 0.1)' }, // 蓝色
  { border: '#9C27B0', bg: 'rgba(156, 39, 176, 0.1)' }, // 紫色
  { border: '#FF9800', bg: 'rgba(255, 152, 0, 0.1)' },  // 橙色
  { border: '#4CAF50', bg: 'rgba(76, 175, 80, 0.1)' },  // 绿色
  { border: '#F44336', bg: 'rgba(244, 67, 54, 0.1)' },  // 红色
  { border: '#00BCD4', bg: 'rgba(0, 188, 212, 0.1)' },  // 青色
  { border: '#795548', bg: 'rgba(121, 85, 72, 0.1)' },  // 棕色
  { border: '#607D8B', bg: 'rgba(96, 125, 139, 0.1)' }  // 蓝灰色
];

// 全局状态管理
let currentSentenceData = null;
let activeLevels = new Set();

// 处理中文翻译文本，添加加粗和下划线样式
function processChineseTranslation(text) {
  if (!text) return text;
  
  // 首先，处理文本中可能存在的 **<u>text</u>** 或 **text** 格式
  // 1. 先处理 **<u>text</u>** 格式
  let processedText = text.replace(/\*\*<u>([^<]*?)<\/u>\*\*/g, function(match, content) {
    return `<strong style="font-weight: bold; color: rgb(28, 28, 28); text-decoration: underline; text-decoration-color: rgb(28, 28, 28); text-underline-offset: 2px;">${content}</strong>`;
  });
  
  // 2. 处理 **text** 格式
  processedText = processedText.replace(/\*\*([^*]*?)\*\*/g, function(match, content) {
    return `<strong style="font-weight: bold; color: rgb(28, 28, 28);">${content}</strong>`;
  });
  
  // 如果文本中没有标记，则使用关键词列表进行处理
  if (processedText === text) {
    // 定义需要加粗和下划线的关键词或短语列表
    const keyPhrases = [
      "传统国家政策和君主间的竞争", "是", "事件的主要因素",
      "民众的意见", "几乎不起作用", "根本不起作用",
      "政治传统、君主个人倾向和他们的竞争", "民众的声音", "变得占主导地位",
      "它", "向君主们指示", "他们的行为",
      "不再", "国家的命运"
    ];
    
    // 对每个关键词或短语应用样式
    keyPhrases.forEach(phrase => {
      if (processedText.includes(phrase)) {
        const styledPhrase = `<strong style="font-weight: bold; color: rgb(28, 28, 28); text-decoration: underline; text-decoration-color: rgb(28, 28, 28); text-underline-offset: 2px;">${phrase}</strong>`;
        // 使用一个不太可能出现在文本中的标记来替换，以避免多次替换同一个短语
        const marker = `###PHRASE_${Math.random().toString(36).substring(2, 15)}###`;
        processedText = processedText.replace(phrase, marker);
        processedText = processedText.replace(marker, styledPhrase);
      }
    });
  }
  
  return processedText;
}

// 处理加粗文本渲染
function processBoldText(text) {
  if (!text) return text;
  
  // 使用更直接的方式处理所有格式
  let result = text;
  
  // 1. 先处理 **<u>text</u>** 格式 - 这是中文翻译使用的格式
  // 使用一个特殊的标记来替换，以避免后续处理干扰
  const boldUnderlineMarker = '###BOLD_UNDERLINE###';
  const boldUnderlineMatches = [];
  
  result = result.replace(/\*\*<u>([^]*?)<\/u>\*\*/g, function(match, content) {
    boldUnderlineMatches.push(content);
    return boldUnderlineMarker + (boldUnderlineMatches.length - 1);
  });
  
  // 2. 处理 **_text_** 格式
  const boldItalicMarker = '###BOLD_ITALIC###';
  const boldItalicMatches = [];
  
  result = result.replace(/\*\*_([^]*?)_\*\*/g, function(match, content) {
    boldItalicMatches.push(content);
    return boldItalicMarker + (boldItalicMatches.length - 1);
  });
  
  // 3. 处理普通的 **text** 格式
  const boldMarker = '###BOLD###';
  const boldMatches = [];
  
  result = result.replace(/\*\*([^]*?)\*\*/g, function(match, content) {
    boldMatches.push(content);
    return boldMarker + (boldMatches.length - 1);
  });
  
  // 4. 恢复所有标记，应用HTML样式
  // 恢复加粗下划线
  for (let i = 0; i < boldUnderlineMatches.length; i++) {
    const marker = boldUnderlineMarker + i;
    result = result.replace(marker, '<strong style="font-weight: bold; color: rgb(28, 28, 28); text-decoration: underline; text-decoration-color: rgb(28, 28, 28); text-underline-offset: 2px;">' + boldUnderlineMatches[i] + '</strong>');
  }
  
  // 恢复加粗斜体
  for (let i = 0; i < boldItalicMatches.length; i++) {
    const marker = boldItalicMarker + i;
    result = result.replace(marker, '<strong style="font-weight: bold; color: rgb(28, 28, 28); text-decoration: underline; text-decoration-color: rgb(28, 28, 28); text-underline-offset: 2px;">' + boldItalicMatches[i] + '</strong>');
  }
  
  // 恢复普通加粗
  for (let i = 0; i < boldMatches.length; i++) {
    const marker = boldMarker + i;
    result = result.replace(marker, '<strong style="font-weight: bold; color: rgb(28, 28, 28);">' + boldMatches[i] + '</strong>');
  }
  

  return result;
}

// 创建加载提示弹窗
function createLoadingModal() {
  const loadingHtml = `
    <div id="loading-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10001;
    ">
      <div style="
        background: white;
        border-radius: 12px;
        padding: 30px;
        text-align: center;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        max-width: 400px;
        width: 90%;
      ">
        <div style="
          width: 50px;
          height: 50px;
          border: 4px solid #e3f2fd;
          border-top: 4px solid #2196f3;
          border-radius: 50%;
          margin: 0 auto 20px;
          animation: spin 1s linear infinite;
        "></div>
        <h3 style="
          margin: 0 0 10px 0;
          color: #2196f3;
          font-size: 18px;
          font-weight: bold;
        ">🤖 AI 正在解析中...</h3>
        <p style="
          margin: 0;
          color: #666;
          font-size: 14px;
          line-height: 1.5;
        ">正在分析英文句子的语法结构<br/>复杂句子可能需要1-2分钟，请耐心等待</p>
        <div style="
          margin-top: 20px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
          border-left: 3px solid #2196f3;
          font-size: 12px;
          color: #666;
        ">
          💡 提示：解析完成后将自动打开分层解析窗口
        </div>
      </div>
    </div>
  `;

  // 添加CSS动画
  if (!document.getElementById('loading-styles')) {
    const styles = document.createElement('style');
    styles.id = 'loading-styles';
    styles.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: scale(0.9); }
        to { opacity: 1; transform: scale(1); }
      }
      #loading-modal > div {
        animation: fadeIn 0.3s ease-out;
      }
    `;
    document.head.appendChild(styles);
  }

  document.body.insertAdjacentHTML('beforeend', loadingHtml);
}

// 移除加载提示弹窗
function removeLoadingModal() {
  const loadingModal = document.getElementById('loading-modal');
  if (loadingModal) {
    loadingModal.style.opacity = '0';
    loadingModal.style.transform = 'scale(0.9)';
    setTimeout(() => {
      loadingModal.remove();
    }, 200);
  }
}

// 创建交互式括号展开组件
function createInteractiveSentence(sentence, allTrans = '', mainTrans = '') {
  // 首先移除任何已存在的弹窗
  const existingModal = document.getElementById('sentence-modal');
  if (existingModal) {
    existingModal.remove();
  }

  // 同时移除可能存在的分享弹窗
  const existingShareModal = document.getElementById('share-preview-modal');
  if (existingShareModal) {
    existingShareModal.remove();
  }

  // 同时移除可能遗留的加载弹窗
  const existingLoadingModal = document.getElementById('loading-modal');
  if (existingLoadingModal) {
    existingLoadingModal.remove();
  }

  // 重置全局状态
  currentSentenceData = parseSentenceStructure(sentence);
  activeLevels.clear();

  // 保存翻译信息到全局变量
  window.currentTranslationData = {
    allTrans,
    mainTrans
  };

  const modalHtml = `
    <div id="sentence-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    ">
      <div style="
        background: white;
        border-radius: 8px;
        padding: 20px;
        width: 800px;
        height: 400px;
        overflow-y: auto;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        position: relative;
        display: flex;
        flex-direction: column;
      ">
        <div style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          border-bottom: 1px solid #f0f0f0;
          padding-bottom: 10px;
        ">
          <div style="display: flex; align-items: center; position: relative;">
            <h3 style="margin: 0; color: #333; font-size: 16px;">英法句子层次化解析</h3>
            <span id="help-icon" style="
              margin-left: 10px;
              width: 16px;
              height: 16px;
              background: #666;
              color: white;
              border-radius: 50%;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              cursor: help;
              position: relative;
            ">?</span>
            <div id="help-tooltip" style="
              position: absolute;
              top: 25px;
              left: 120px;
              background: white;
              color: #333;
              padding: 15px;
              border-radius: 8px;
              font-size: 12px;
              width: 350px;
              z-index: 10001;
              display: none;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
              border: 1px solid #e0e0e0;
            ">
              <div style="margin-bottom: 10px;">
                <span style="font-weight: bold;">💡 使用说明：</span>
                点击 <span style="color: #007bff; font-weight: bold;">(...)</span> 用彩色边框高亮显示对应的句子成分
              </div>
              <div style="margin-bottom: 8px; font-weight: bold; color: #333;">📊 层级颜色：</div>
              <div style="display: flex; flex-wrap: wrap; gap: 6px;">
                ${levelColors.map((color, index) => `
                  <span style="
                    display: inline-flex;
                    align-items: center;
                    padding: 2px 6px;
                    border: 1px solid ${color.border};
                    background: ${color.bg};
                    border-radius: 3px;
                    font-size: 10px;
                    font-weight: bold;
                  ">L${index + 1}</span>
                `).join('')}
              </div>
            </div>
          </div>
          <button id="close-modal" style="
            background: none;
            border: none;
            font-size: 20px;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: background 0.2s;
          " onmouseover="this.style.background='#f0f0f0'" onmouseout="this.style.background='none'">×</button>
        </div>
        
        <div id="sentence-content" style="
          font-size: 14px;
          line-height: 1.6;
          color: #333;
          font-family: 'Georgia', serif;
          padding: 15px;
          background: #fafafa;
          border-radius: 6px;
          border: 1px solid #e0e0e0;
          position: relative;
          min-height: 150px;
          flex: 1;
          overflow-y: auto;
        "></div>
        
        <div id="translation-content" style="
          margin-top: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 6px;
          border: 1px solid #e9ecef;
          border-left: 4px solid #28a745;
          display: none;
        ">
          <div style="
            font-weight: bold;
            color: #28a745;
            margin-bottom: 10px;
            font-size: 14px;
          ">📖 中文翻译</div>
          <div id="translation-text" style="
            color: #495057;
            font-size: 13px;
            line-height: 1.6;
            font-family: 'Microsoft YaHei', sans-serif;
          "></div>
        </div>
        
        <div style="
          margin-top: 15px;
          padding-top: 10px;
          border-top: 1px solid #e0e0e0;
          text-align: center;
        ">
          <button id="clear-highlights" style="
            background: #2196f3;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            margin-right: 8px;
            transition: background 0.2s;
          ">句子主干</button>
          
          <button id="show-all-levels" style="
            background: #ccc;
            color: #666;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            margin-right: 8px;
            transition: background 0.2s;
          ">结构关系</button>
          
          <button id="show-full-sentence" style="
            background: #ccc;
            color: #666;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            transition: background 0.2s;
          ">原句</button>

          <button id="share-sentence" style="
            background: #4CAF50;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            margin-left: 8px;
            transition: background 0.2s;
          ">分享</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  // 绑定帮助提示事件
  const helpIcon = document.getElementById('help-icon');
  const helpTooltip = document.getElementById('help-tooltip');

  helpIcon.addEventListener('mouseenter', function () {
    helpTooltip.style.display = 'block';
  });

  helpIcon.addEventListener('mouseleave', function () {
    helpTooltip.style.display = 'none';
  });

  renderSentence();
  // 传递原始句子（没有括号的版本）给事件绑定函数
  const originalSentence = sentence.replace(/[()]/g, '');
  bindModalEvents(originalSentence);
}

// 解析句子结构，将括号转换为嵌套结构
function parseSentenceStructure(sentence) {
  let idCounter = 0;

  function parseLevel(text, level = 0, startIndex = 0) {
    const parts = [];
    let currentText = '';
    let i = 0;

    while (i < text.length) {
      const char = text[i];

      if (char === '(') {
        // 保存当前文本
        if (currentText.trim()) {
          parts.push({
            type: 'text',
            content: currentText.trim(),
            level
          });
          currentText = '';
        }

        // 查找匹配的右括号
        let depth = 1;
        let j = i + 1;
        let bracketContent = '';

        while (j < text.length && depth > 0) {
          if (text[j] === '(') depth++;
          if (text[j] === ')') depth--;
          if (depth > 0) bracketContent += text[j];
          j++;
        }

        if (depth === 0) {
          // 递归解析括号内容
          const children = parseLevel(bracketContent, level + 1, startIndex + i + 1);

          parts.push({
            type: 'bracket',
            content: bracketContent,
            children,
            level,
            id: idCounter++,
            startIndex: startIndex + i,
            endIndex: startIndex + j - 1
          });

          i = j;
        } else {
          // 未匹配的左括号，作为普通文本处理
          currentText += char;
          i++;
        }
      } else if (char === ')') {
        // 未匹配的右括号，作为普通文本处理
        currentText += char;
        i++;
      } else {
        currentText += char;
        i++;
      }
    }

    // 保存剩余文本
    if (currentText.trim()) {
      parts.push({
        type: 'text',
        content: currentText.trim(),
        level
      });
    }

    return parts;
  }

  return parseLevel(sentence);
}

// 渲染句子内容
function renderSentence() {
  const container = document.getElementById('sentence-content');
  if (!container) return;

  function renderParts(parts) {
    return parts.map(part => {
      if (part.type === 'text') {
        return processBoldText(part.content);
      } else if (part.type === 'bracket') {
        const isActive = activeLevels.has(part.id);
        const color = levelColors[part.level % levelColors.length];

        if (isActive) {
          // 展开状态：显示括号内容并添加边框
          const childrenContent = renderParts(part.children || []);
          return `<span class="level-toggle active" data-id="${part.id}" style="
            border: 2px solid ${color.border};
            background: ${color.bg};
            padding: 2px 4px;
            border-radius: 3px;
            margin: 0 1px;
            position: relative;
            display: inline-block;
            cursor: pointer;
          ">
            ${childrenContent}
          </span>`;
        } else {
          // 折叠状态：显示可点击的省略号
          return `<span class="level-toggle" data-id="${part.id}" style="
            background: ${color.bg};
            border: 1px solid ${color.border};
            color: ${color.border};
            padding: 1px 4px;
            border-radius: 3px;
            cursor: pointer;
            font-weight: bold;
            transition: all 0.2s;
            position: relative;
            display: inline-block;
            margin: 0 1px;
          " onmouseover="this.style.background='${color.border}'; this.style.color='white';" onmouseout="this.style.background='${color.bg}'; this.style.color='${color.border}';">
            (...)
          </span>`;
        }
      }
      return '';
    }).join('');
  }

  container.innerHTML = renderParts(currentSentenceData);

  // 如果是句子主干视图，则显示主干翻译
  if (activeLevels.size === 0 && window.currentTranslationData && window.currentTranslationData.mainTrans) {
    let html = `<div style='width: 100%; display: flex; justify-content: center;'><div style="
      height: 1px;
      background: #888888;
      width: 120px;
      margin: 12px 0 12px 0;
    "></div></div>`;
    html += `<div style="
      color: #222;
      font-size: 13px;
      line-height: 1.7;
      margin-top: 0;
      font-weight: normal;
      text-align: left;
      margin-left: 0px;
    ">
      ${processChineseTranslation(window.currentTranslationData.mainTrans)}
    </div>`;
    container.innerHTML += html;
  }

  // 绑定点击事件
  container.querySelectorAll('.level-toggle').forEach(toggle => {
    toggle.addEventListener('click', function (e) {
      e.stopPropagation();
      const id = parseInt(this.dataset.id);

      if (this.classList.contains('active') || activeLevels.has(id)) {
        // 如果是激活状态，则收起
        activeLevels.delete(id);
      } else {
        // 如果是折叠状态，则展开
        activeLevels.add(id);
      }

      renderSentence();
    });
  });
}

// 显示所有层级
function showAllLevels() {
  function addAllBrackets(parts) {
    parts.forEach(part => {
      if (part.type === 'bracket') {
        activeLevels.add(part.id);
        if (part.children) {
          addAllBrackets(part.children);
        }
      }
    });
  }

  addAllBrackets(currentSentenceData);
  renderSentence();
}

// 从解析结构中提取句子主干
function getMainSentence(parts) {
  if (!parts) return ''
  return parts
    .map(part => {
      if (part.type === 'text') {
        return part.content
      } else if (part.type === 'bracket') {
        return '(...)'
      }
      return ''
    })
    .join(' ')
    .replace(/ \./g, '.')
    .replace(/ ,/g, ',')
}

// 创建并显示分享图片
async function createShareImage(originalSentence, mainTrans, allTrans) {
  // 1. 收集需要展示的数据
  const mainSentence = getMainSentence(currentSentenceData)

  try {
    // 获取原始解析结果数据
    const originalResult = window.lastEnglishParseResult || {}
    console.log('=== 准备发送的数据 ===')
    console.log('原始句子:', originalSentence)
    console.log('coze数据:', originalResult)
    
    // 准备要保存的数据 - 确保coze_data是原始字符串，而不是嵌套的JSON
    let cozeData = ''
    
    if (originalResult.data) {
      // 如果是原始的Coze响应，提取data字段
      cozeData = typeof originalResult.data === 'string' 
        ? originalResult.data 
        : JSON.stringify(originalResult.data)
    } else {
      // 如果是其他格式，直接序列化整个对象
      cozeData = JSON.stringify(originalResult)
    }
    
    const sentenceData = {
      sentences_content: originalSentence, // 原始句子
      coze_data: cozeData // coze返回的数据，确保是字符串
    }

    console.log('最终发送的数据:', sentenceData)

    // 显示加载中提示
    const loadingHtml = `
      <div id="saving-modal" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10003;
      ">
        <div style="
          background: white;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        ">
          <div style="
            width: 30px;
            height: 30px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #3498db;
            border-radius: 50%;
            margin: 0 auto 15px;
            animation: spin 1s linear infinite;
          "></div>
          <p style="margin: 0; color: #666;">保存中...</p>
        </div>
      </div>
    `
    document.body.insertAdjacentHTML('beforeend', loadingHtml)

    // 调用保存接口
    console.log('发送请求数据:', sentenceData)
    const response = await fetch('/api/Paper/saveSentences', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authorization')
      },
      body: JSON.stringify(sentenceData)
    })

    // 移除加载提示
    const savingModal = document.getElementById('saving-modal')
    if (savingModal) {
      savingModal.remove()
    }

    if (!response.ok) {
      throw new Error('保存失败，请重试')
    }

    const result = await response.json()
    console.log('接口返回结果:', result)
    
    if (result.code !== 200) {
      throw new Error(result.message || '保存失败，请重试')
    }

    // 获取sentence_id
    const sentenceId = result.result?.sentences_id || result.result
    if (!sentenceId) {
      throw new Error('获取句子ID失败')
    }

    // 2. 创建一个用于生成图片的、屏幕外的容器
    const container = document.createElement('div')
    container.id = 'share-image-container'
    Object.assign(container.style, {
      position: 'absolute',
      top: '-9999px',
      left: '-9999px',
      width: '350px', // 为竖版图片设定一个合适的宽度
      padding: '25px',
      background: 'white',
      border: '1px solid #dee2e6',
      fontFamily: "'Microsoft YaHei', sans-serif"
    })

    // 3. 生成包含sentence_id的二维码链接
    const shareUrl = `${window.location.origin}/#/editor/note?sentence_id=${sentenceId}`
    const qrCodeDataURL = await QRCode.toDataURL(shareUrl, {
      width: 100,
      margin: 1,
      color: {
        dark: '#000000',
        light: '#FFFFFFFF'
      }
    })

    // 4. 构建用于生成图片的HTML内容
    container.innerHTML = `
      <style>
        #share-image-container * {
          box-sizing: border-box;
        }
        #share-image-container h3 {
          margin: 0 0 20px 0;
          font-size: 20px;
          font-weight: 600;
          color: #343a40;
          text-align: center;
          border-bottom: 1px solid #f1f3f5;
          padding-bottom: 15px;
        }
        #share-image-container .section {
          margin-bottom: 20px;
        }
        #share-image-container .title {
          font-weight: bold;
          font-size: 16px;
          color: #1971c2;
          margin-bottom: 10px;
        }
        #share-image-container .content {
          font-size: 15px;
          color: #495057;
          line-height: 1.7;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 6px;
        }
        #share-image-container .trans {
          font-size: 14px;
          color: #343a40;
          line-height: 1.7;
          padding: 12px;
          background: #e7f5ff;
          border-left: 3px solid #1971c2;
          margin-top: 10px;
          border-radius: 0 6px 6px 0;
        }
        #share-image-container .footer {
          margin-top: 25px;
          padding-top: 20px;
          border-top: 1px solid #f1f3f5;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        #share-image-container .qr-code {
          width: 100px;
          height: 100px;
        }
        #share-image-container .qr-text {
          font-size: 14px;
          color: #495057;
          margin-left: 20px;
          text-align: left;
          line-height: 1.5;
        }
        #share-image-container strong {
          font-weight: bold;
          color: rgb(28, 28, 28);
          text-decoration: underline;
          text-decoration-color: rgb(28, 28, 28);
          text-underline-offset: 2px;
        }
      </style>
      <h3>英法长难句解析</h3>
      <div class="section">
        <div class="title">句子主干</div>
        <div class="content">${processBoldText(mainSentence)}</div>
        <div class="trans">${processBoldText(originalSentence)}</div>
      </div>
      <div class="footer">
        <img src="${qrCodeDataURL}" class="qr-code" alt="QR Code"/>
        <div class="qr-text">
          <div style="font-size: 16px;  margin-bottom: 5px;">【外语神器】</div>
          <div>  扫码或长按识别</div>
          <div>  跳转查看详细解析</div>
        </div>
      </div>
    `

    // 5. 将容器添加到DOM中，使用html2canvas截图，然后移除
    document.body.appendChild(container)
    try {
      const canvas = await html2canvas(container, {
        useCORS: true,
        scale: 2 // 提高图片分辨率
      })

      const imageUrl = canvas.toDataURL('image/png')
      const shareModalHtml = `
        <div id="share-preview-modal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.75); display: flex; justify-content: center; align-items: center; z-index: 10002; animation: fadeIn 0.3s ease-out;">
          <div style="background: white; padding: 25px; border-radius: 12px; text-align: center; position: relative; box-shadow: 0 8px 30px rgba(0,0,0,0.2);">
            <button id="close-share-modal" style="position: absolute; top: 8px; right: 8px; background: none; border: none; font-size: 24px; cursor: pointer; color: #888; padding: 5px;">×</button>
            <h3 style="margin-top: 0; margin-bottom: 20px; color: #333; font-size: 16px;">分享给好友</h3>
            <img src="${imageUrl}" style="max-width: 90vw; max-height: 70vh; border-radius: 8px; border: 1px solid #eee;" alt="分享图片"/>
            <p style="color: #666; font-size: 13px; margin-top: 20px; margin-bottom: 0;">请右键保存图片，然后分享</p>
          </div>
        </div>
      `
      document.body.insertAdjacentHTML('beforeend', shareModalHtml)

      // 绑定关闭事件
      const shareModal = document.getElementById('share-preview-modal')
      shareModal.querySelector('#close-share-modal').addEventListener('click', () => shareModal.remove())
      shareModal.addEventListener('click', (e) => {
        if (e.target.id === 'share-preview-modal') shareModal.remove()
      })
    } catch (error) {
      console.error('生成分享图片失败:', error)
      const errorMessage = `
<div style="background: #fef0f0; padding: 15px; border-radius: 8px; border-left: 4px solid #f56c6c;">
  <div style="font-weight: bold; color: #f56c6c; margin-bottom: 10px;">❌ 分享图片生成失败</div>
  <div style="color: #666; font-size: 13px;">${error.message}</div>
</div>`
      const existingCallback = window.englishParseCallback
      if (existingCallback) existingCallback(errorMessage)
    }
  } catch (error) {
    console.error('分享失败:', error)
    const errorMessage = `
<div style="background: #fef0f0; padding: 15px; border-radius: 8px; border-left: 4px solid #f56c6c;">
  <div style="font-weight: bold; color: #f56c6c; margin-bottom: 10px;">❌ 分享失败</div>
  <div style="color: #666; font-size: 13px;">${error.message}</div>
</div>`
    const existingCallback = window.englishParseCallback
    if (existingCallback) existingCallback(errorMessage)
  } finally {
    // 6. 清理DOM
    const container = document.getElementById('share-image-container')
    if (container) {
      document.body.removeChild(container)
    }
  }
}

// 从URL参数中获取sentence_id并加载句子数据
async function loadSentenceFromId(sentenceId) {
  if (!sentenceId) return null
  
  try {
    // 显示加载中提示
    createLoadingModal()
    
    console.log('正在加载句子数据，ID:', sentenceId)
    
    // 调用接口获取句子数据
    const response = await fetch(`/api/Paper/getSentencesInfo?sentences_id=${sentenceId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': localStorage.getItem('authorization')
      }
    })
    
    if (!response.ok) {
      throw new Error('获取句子数据失败')
    }
    
    const result = await response.json()
    console.log('接口返回结果:', result)
    
    if (result.code !== 200) {
      throw new Error(result.message || '获取句子数据失败')
    }
    
    // 解析返回的数据
    const sentenceData = result.result
    if (!sentenceData) {
      throw new Error('未找到句子数据')
    }
    
    // 提取原始句子和解析结果
    const originalSentence = sentenceData.sentences_content
    let cozeResult = {}
    
    try {
      // 首先尝试从sentences_coze_content字段解析数据
      if (sentenceData.sentences_coze_content) {
        try {
          // 尝试解析为JSON对象
          cozeResult = JSON.parse(sentenceData.sentences_coze_content)
          console.log('从sentences_coze_content解析的数据:', cozeResult)
        } catch (e) {
          console.warn('sentences_coze_content不是有效的JSON，作为普通文本处理')
          cozeResult = { sentence: sentenceData.sentences_coze_content }
        }
      } 
      // 如果没有sentences_coze_content字段，尝试从coze_data字段解析
      else if (sentenceData.coze_data) {
        try {
          cozeResult = JSON.parse(sentenceData.coze_data)
          console.log('从coze_data解析的数据:', cozeResult)
        } catch (e) {
          console.warn('coze_data不是有效的JSON，作为普通文本处理')
          cozeResult = { data: sentenceData.coze_data }
        }
      } else {
        throw new Error('未找到解析数据')
      }
      
      // 保存原始解析结果，用于可能的再次分享
      window.lastEnglishParseResult = cozeResult
      window.originalSentence = originalSentence
    } catch (e) {
      console.error('解析数据失败:', e)
      throw new Error('解析数据格式错误: ' + e.message)
    }
    
    // 提取句子结构和翻译信息
    const resultData = extractSentenceFromResult(cozeResult)
    console.log('提取的句子结构:', resultData)
    
    // 移除加载提示
    removeLoadingModal()
    
    // 创建交互式句子展示
    if (resultData && resultData.sentence) {
      createInteractiveSentence(
        resultData.sentence,
        resultData.allTrans || '',
        resultData.mainTrans || ''
      )
      return true
    } else {
      throw new Error('无法提取句子结构，提取结果: ' + JSON.stringify(resultData))
    }
  } catch (error) {
    console.error('加载句子数据失败:', error)
    removeLoadingModal()
    
    // 显示错误提示
    const errorMessage = `
<div style="background: #fef0f0; padding: 15px; border-radius: 8px; border-left: 4px solid #f56c6c;">
  <div style="font-weight: bold; color: #f56c6c; margin-bottom: 10px;">❌ 加载句子数据失败</div>
  <div style="color: #666; font-size: 13px;">${error.message}</div>
</div>`
    const existingCallback = window.englishParseCallback
    if (existingCallback) existingCallback(errorMessage)
    
    return false
  }
}

// 绑定弹框事件
function bindModalEvents(originalSentence) {
  // 按钮状态管理 - 四个按钮互斥，只有一个保持激活
  let activeButton = 'clear-highlights'; // 默认选中清除高亮

  function setActiveButton(buttonId) {
    // 重置所有按钮状态
    document.querySelectorAll('#clear-highlights, #show-all-levels, #show-full-sentence').forEach(btn => {
      btn.style.background = '#ccc';
      btn.style.color = '#666';
    });

    // 设置当前按钮为激活状态
    const button = document.getElementById(buttonId);
    if (button) {
      button.style.background = '#2196f3';
      button.style.color = 'white';
      activeButton = buttonId;
    }
  }

  // 关闭弹框
  document.getElementById('close-modal').addEventListener('click', function () {
    document.getElementById('sentence-modal').remove();
  });

  // 点击背景关闭
  document.getElementById('sentence-modal').addEventListener('click', function (e) {
    if (e.target.id === 'sentence-modal') {
      this.remove();
    }
  });

  // 清除所有高亮
  document.getElementById('clear-highlights').addEventListener('click', function () {
    activeLevels.clear();
    renderSentence();

    // 隐藏翻译区域
    const translationContent = document.getElementById('translation-content');
    if (translationContent) {
      translationContent.style.display = 'none';
    }

    setActiveButton('clear-highlights');
  });

  // 显示所有层级
  document.getElementById('show-all-levels').addEventListener('click', function () {
    showAllLevels();

    // 隐藏翻译区域
    const translationContent = document.getElementById('translation-content');
    if (translationContent) {
      translationContent.style.display = 'none';
    }

    setActiveButton('show-all-levels');
  });

  // 显示完整句子
  document.getElementById('show-full-sentence').addEventListener('click', function () {
    const container = document.getElementById('sentence-content');
    if (container) {
      let html = `<div style="
        padding: 10px;
        border-radius: 4px;
        line-height: 1.5;
        font-size: 13px;
        font-weight: normal;
        margin-bottom: 10px;">
        ${processBoldText(originalSentence)}
      </div>`;
      // 取翻译内容
      if (window.currentTranslationData && window.currentTranslationData.allTrans) {
        
        // 由于 allTrans 中没有 **<u>text</u>** 格式的标记，我们需要手动添加样式
        // 使用全局的 processChineseTranslation 函数来处理中文翻译
        
        const processedAllTrans = processChineseTranslation(window.currentTranslationData.allTrans);
        
        html += `<div style='width: 100%; display: flex; justify-content: center;'><div style="
          height: 1px;
          background: #888888;
          width: 120px;
          margin: 12px 0 12px 0;
        "></div></div>`;
        html += `<div style="
          color: #222;
          font-size: 13px;
          line-height: 1.7;
          margin-top: 0;
          font-weight: normal;
          text-align: left;
          margin-left: 10px;
        ">
          ${processedAllTrans}
        </div>`;
      }
      container.innerHTML = html;
    }
    setActiveButton('show-full-sentence');
  });

  // 分享按钮
  document.getElementById('share-sentence').addEventListener('click', function () {
    createShareImage(originalSentence, window.currentTranslationData.mainTrans, window.currentTranslationData.allTrans)
  })

  // 初始时，手动触发 "句子主干" 按钮的逻辑，以显示主干翻译
  document.getElementById('clear-highlights').click();

  // 监听句子内容区域的点击，如果点击了(...) 则重置为清除高亮状态
  document.getElementById('sentence-content').addEventListener('click', function (e) {
    if (e.target.classList.contains('level-toggle')) {
      setActiveButton('clear-highlights');
    }
  });

  // ESC键关闭
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      const modal = document.getElementById('sentence-modal');
      if (modal) {
        modal.remove();
      }
    }
  });
}

export {
  loadSentenceFromId
}

export default class EnglishParse extends OriginalBold {
  get name() {
    return 'englishParse'
  }
  
  menuBtnView({ isActive, commands, focus, editor }) {
    return {
      component: CustomEnglishParseMenuButton,
      componentProps: {
        isActive: false // 英法句子分析不需要激活状态
      },
      componentEvents: {
        click: async (callback) => {
          window.englishParseCallback = callback // Store callback
          const selection = window.getSelection()
          const selectedText = selection.toString().trim()

          // 如果没有选中文本，提示用户
          if (!selectedText) {
            callback && callback('请先选中要解析的文本')
            return
          }

          // 检查是否包含英文
          if (!containsEnglish(selectedText)) {
            callback && callback('选中的文本不包含英文，无法解析')
            return
          }

          try {
            // 显示加载提示弹窗
            createLoadingModal()

            // 调用智能体解析英文句子
            const result = await parseEnglishSentence(selectedText)
            
            // 保存原始解析结果，用于分享功能
            window.lastEnglishParseResult = result
            window.originalSentence = selectedText

            // 移除加载提示
            removeLoadingModal()

            // 在控制台打印完整的解析结果
            console.log('=== 英文句子语法解析结果 ===')
            console.log(result)
            
            // 打印完整的JSON字符串，方便复制
            console.log('=== 完整JSON字符串 ===')
            console.log(JSON.stringify(result, null, 2))

            // 提取句子并创建交互式展示
            const resultData = extractSentenceFromResult(result)
            console.log('提取的句子结构:', resultData)
            
            if (resultData && resultData.sentence && resultData.sentence.trim() !== '') {
              // 如果句子没有括号结构，我们为关键部分添加括号以支持交互功能
              let processedSentence = resultData.sentence;
              if (!processedSentence.includes('(') || !processedSentence.includes(')')) {
                // 检查是否有加粗或下划线标记，这些通常是重要部分
                const boldPattern = /\*\*([^*]+)\*\*/g;
                const underlinePattern = /<u>([^<]+)<\/u>/g;
                
                // 为加粗文本添加括号
                processedSentence = processedSentence.replace(boldPattern, '(**$1**)');
                // 为下划线文本添加括号
                processedSentence = processedSentence.replace(underlinePattern, '(<u>$1</u>)');
              }
              
              // 创建交互式句子展示
              createInteractiveSentence(processedSentence, resultData.allTrans, resultData.mainTrans)

              // 显示成功提示
              const successMessage = `
<div style="background: #f0f9ff; padding: 15px; border-radius: 8px; border-left: 4px solid #409EFF;">
  <div style="font-weight: bold; color: #409EFF; margin-bottom: 10px;">✅ 解析完成</div>
  <div style="color: #666; font-size: 14px;">已打开分层解析窗口，点击括号查看彩色边框高亮效果！</div>
</div>`;

              callback && callback(successMessage)
            } else {
              // 如果没有括号，显示提取失败信息
              const errorMessage = `
<div style="background: #fef0f0; padding: 15px; border-radius: 8px; border-left: 4px solid #f56c6c;">
  <div style="font-weight: bold; color: #f56c6c; margin-bottom: 10px;">❌ 解析结果格式不正确</div>
  <div style="color: #666; font-size: 14px;">无法提取句子结构，请检查控制台日志或联系管理员</div>
</div>`;

              callback && callback(errorMessage)
            }

          } catch (error) {
            // 出错时也要移除加载提示
            removeLoadingModal()

            const errorMessage = `
<div style="background: #fef0f0; padding: 15px; border-radius: 8px; border-left: 4px solid #f56c6c;">
  <div style="font-weight: bold; color: #f56c6c; margin-bottom: 10px;">❌ 解析失败</div>
  <div style="color: #666;">${error.message}</div>
</div>`;
            callback && callback(errorMessage)
          }
        }
      }
    }
  }
  
  // 在组件初始化时检查URL参数
  constructor(options) {
    super(options)
    
    // 在DOM加载完成后执行
    document.addEventListener('DOMContentLoaded', () => {
      // 解析URL参数
      const urlParams = new URLSearchParams(window.location.search)
      const sentenceId = urlParams.get('sentence_id')
      
      // 如果存在sentence_id参数，则加载句子数据
      if (sentenceId) {
        loadSentenceFromId(sentenceId)
      }
    })
  }
} 